import React, { useState, useEffect, useRef } from 'react';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import './AuthPage.css';

const API_URL   = 'https://mykaddle-api.onrender.com/api/auth';
const TUTOR_URL = 'https://mykaddle-api.onrender.com/api/tutor';

// ─── Viewport Detection ───────────────────────────────────────────────────────
const isSmallViewport = () =>
  typeof window !== 'undefined' && window.innerWidth < 768;

const getInitials = (name = '') =>
  name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

// ─── Mobile Landing Screen (mbl- prefix) ────────────────────────────────────
const MobileLandingScreen = () => {
  const [role, setRole] = useState('student');
  const [modalOpen, setModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // ── Real tutor data from API ──
  const [tutors, setTutors] = useState([]);
  const [tutorLoading, setTutorLoading] = useState(true);
  const [tutorError, setTutorError] = useState(false);

  // ── FIX: Enable body scroll when mobile landing is active ──
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;

    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.height = originalHeight;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    axios.get(`${TUTOR_URL}/public`)
      .then(res => {
        const valid = res.data.filter(t => t.bio && t.bio.trim().length > 0);
        setTutors(valid);
      })
      .catch(() => setTutorError(true))
      .finally(() => setTutorLoading(false));
  }, []);

  const switchRole = (r) => {
    setRole(r);
    window.scrollTo(0, 0);
  };

  const showModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) closeModal();
  };

  return (
    <div className="mbl-app-shell">
      {/* Header */}
      <nav className={`mbl-top-nav ${scrolled ? 'mbl-scrolled' : ''}`}>
        <div className="mbl-brand">
          <span className="mbl-brand-dot"></span>
          Kaddle
        </div>
        <div className="mbl-role-toggle">
          <button
            className={`mbl-toggle-btn ${role === 'student' ? 'mbl-active' : ''}`}
            onClick={() => switchRole('student')}
          >
            Student
          </button>
          <button
            className={`mbl-toggle-btn ${role === 'tutor' ? 'mbl-active' : ''}`}
            onClick={() => switchRole('tutor')}
          >
            Tutor
          </button>
        </div>
      </nav>

      {/* =============== STUDENT VIEW =============== */}
      {role === 'student' && (
        <div className="mbl-student-view">

          {/* Hero */}
          <section className="mbl-hero mbl-animate-in">
            <div className="mbl-hero-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
              Find your perfect tutor
            </div>
            <h1>Personalized learning. <span>Your way.</span></h1>
            <p>Join group studies or request 1-on-1 sessions. Browse tutors, compare rates, and start learning today. Affordable, transparent, no surprises.</p>
            <div className="mbl-hero-actions">
              <a href="#mbl-tutors" className="mbl-btn mbl-btn-primary">
                Browse Tutors
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </a>
            </div>
          </section>

          {/* Comparison */}
          <section className="mbl-comparison-section">
            <div className="mbl-comparison-title">Why choose MyKaddle?</div>
            <div className="mbl-comparison-grid">
              <div className="mbl-comparison-card">
                <div className="mbl-comparison-label">Care.com</div>
                <div className="mbl-comparison-value">Limited profile info, hidden pricing, slow support</div>
              </div>
              <div className="mbl-comparison-card mbl-kaddle">
                <div className="mbl-comparison-label">MyKaddle</div>
                <div className="mbl-comparison-value">Full tutor profiles, transparent rates, instant messaging</div>
              </div>
              <div className="mbl-comparison-card">
                <div className="mbl-comparison-label">Tutor.com</div>
                <div className="mbl-comparison-value">Expensive sessions, rigid scheduling, platform-only</div>
              </div>
              <div className="mbl-comparison-card mbl-kaddle">
                <div className="mbl-comparison-label">MyKaddle</div>
                <div className="mbl-comparison-value">Flexible rates, your schedule, groups or 1-on-1</div>
              </div>
            </div>
          </section>

          {/* Points - Find Your Fit */}
          <section className="mbl-section mbl-animate-in mbl-delay-1">
            <div className="mbl-section-header">
              <h2 className="mbl-section-title">Your goals. Your budget. Your tutor.</h2>
            </div>
            <div className="mbl-points-list">
              <div className="mbl-point-item">
                <div className="mbl-point-icon mbl-icon-lime">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <circle cx="12" cy="12" r="6"/>
                    <circle cx="12" cy="12" r="2"/>
                  </svg>
                </div>
                <div className="mbl-point-body">
                  <h4>Find Your Fit</h4>
                  <p>Join tutoring groups that match your fit.</p>
                </div>
              </div>
              <div className="mbl-point-item">
                <div className="mbl-point-icon mbl-icon-blue">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z"/>
                  </svg>
                </div>
                <div className="mbl-point-body">
                  <h4>Try Before Committing</h4>
                  <p>Take advantage of the free trials to see if the group is right for you.</p>
                </div>
              </div>
              <div className="mbl-point-item">
                <div className="mbl-point-icon mbl-icon-orange">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <div className="mbl-point-body">
                  <h4>Flexible Subscriptions</h4>
                  <p>Subscribe weekly or monthly depending on what the tutor offers.</p>
                </div>
              </div>
              <div className="mbl-point-item">
                <div className="mbl-point-icon mbl-icon-green">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                </div>
                <div className="mbl-point-body">
                  <h4>Can't Find What You Need?</h4>
                  <p>Send a tutoring request with your dates, what you need help with, and your weekly budget.</p>
                </div>
              </div>
              <div className="mbl-point-item">
                <div className="mbl-point-icon mbl-icon-purple">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <div className="mbl-point-body">
                  <h4>Secure Payments</h4>
                  <p>Your payments are protected under our payment and refund policies.</p>
                </div>
              </div>
              <div className="mbl-point-item">
                <div className="mbl-point-icon mbl-icon-lime">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                    <line x1="12" y1="18" x2="12.01" y2="18"/>
                  </svg>
                </div>
                <div className="mbl-point-body">
                  <h4>Everything in One Place</h4>
                  <p>Access lessons, schedules, group chat, files and payments without having to juggle multiple platforms.</p>
                </div>
              </div>
            </div>
          </section>

          {/* How it works */}
          <section className="mbl-section">
            <div className="mbl-section-header">
              <h2 className="mbl-section-title">How it works</h2>
            </div>
            <div className="mbl-steps">
              <div className="mbl-step">
                <div className="mbl-step-num">1</div>
                <div className="mbl-step-body">
                  <h4>Browse or Request</h4>
                  <p>Search by subject, or send a private request with your budget &amp; availability.</p>
                </div>
              </div>
              <div className="mbl-step">
                <div className="mbl-step-num">2</div>
                <div className="mbl-step-body">
                  <h4>Get Matched</h4>
                  <p>Tutors accept your request. You're added to a group or 1-on-1 instantly.</p>
                </div>
              </div>
              <div className="mbl-step">
                <div className="mbl-step-num">3</div>
                <div className="mbl-step-body">
                  <h4>Start Learning</h4>
                  <p>Chat, attend sessions, track progress, manage payments—all in-app.</p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="mbl-cta-banner mbl-student">
            <h3>Ready to ace your classes?</h3>
            <p>Sign up now and find a tutor who matches your learning style. First week risk-free.</p>
            <button className="mbl-btn-lime" onClick={showModal}>Start Learning Today</button>
          </section>

        </div>
      )}

      {/* =============== TUTOR VIEW =============== */}
      {role === 'tutor' && (
        <div className="mbl-tutor-view">

          {/* Hero */}
          <section className="mbl-hero mbl-animate-in">
            <div className="mbl-hero-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 2 3 4 6 4s6-2 6-4v-5"/>
              </svg>
              Earn more, teach your way
            </div>
            <h1>Run your own tutoring <span>business.</span></h1>
            <p>Set your rates, create flexible groups, accept private requests. Keep 85% of earnings. No admin overhead, no commission negotiation. Your profile, your brand, your growth.</p>
            <div className="mbl-hero-actions">
              <a
                href="https://forms.gle/QwWVCDrKL3gbPoYq5"
                target="_blank"
                rel="noopener noreferrer"
                className="mbl-btn mbl-btn-primary"
              >
                Apply Now
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </a>
            </div>
          </section>

          {/* Comparison */}
          <section className="mbl-comparison-section">
            <div className="mbl-comparison-title">Why tutors switch to MyKaddle</div>
            <div className="mbl-comparison-grid">
              <div className="mbl-comparison-card">
                <div className="mbl-comparison-label">Care.com</div>
                <div className="mbl-comparison-value">20–30% cut, heavy admin, buried profiles</div>
              </div>
              <div className="mbl-comparison-card mbl-kaddle">
                <div className="mbl-comparison-label">MyKaddle</div>
                <div className="mbl-comparison-value"><strong>85% cut</strong>, zero admin, shareable profile</div>
              </div>
              <div className="mbl-comparison-card">
                <div className="mbl-comparison-label">Tutor.com</div>
                <div className="mbl-comparison-value">Limited rates, no group control, platform lock-in</div>
              </div>
              <div className="mbl-comparison-card mbl-kaddle">
                <div className="mbl-comparison-label">MyKaddle</div>
                <div className="mbl-comparison-value">Set your rates, 3 groups, own your clients</div>
              </div>
              <div className="mbl-comparison-card">
                <div className="mbl-comparison-label">Facebook Groups</div>
                <div className="mbl-comparison-value">Scattered, no payments, unprofessional</div>
              </div>
              <div className="mbl-comparison-card mbl-kaddle">
                <div className="mbl-comparison-label">MyKaddle</div>
                <div className="mbl-comparison-value">Organized, in-app wallet, professional presence</div>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="mbl-section mbl-animate-in mbl-delay-2">
            <div className="mbl-section-header">
              <h2 className="mbl-section-title">Build your tutoring empire</h2>
            </div>
            <div className="mbl-feature-grid">
              <div className="mbl-feature-card">
                <div className="mbl-feature-icon mbl-icon-lime">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
                  </svg>
                </div>
                <div className="mbl-feature-body">
                  <h3>Create 3 Study Groups</h3>
                  <p>Set your own rates (weekly or monthly billing). Students subscribe and stay in your groups. Recurring revenue, simplified.</p>
                </div>
              </div>

              <div className="mbl-feature-card">
                <div className="mbl-feature-icon mbl-icon-blue">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </div>
                <div className="mbl-feature-body">
                  <h3>Accept Private Requests</h3>
                  <p>Students send 1-on-1 requests with budgets. You accept or decline. Build multiple income streams.</p>
                </div>
              </div>

              <div className="mbl-feature-card">
                <div className="mbl-feature-icon mbl-icon-orange">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <div className="mbl-feature-body">
                  <h3>Build Your Reputation</h3>
                  <p>Earn reviews and likes that stick to your profile. Better reputation = more student requests. Grow organically.</p>
                </div>
              </div>

              <div className="mbl-feature-card">
                <div className="mbl-feature-icon mbl-icon-green">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                </div>
                <div className="mbl-feature-body">
                  <h3>Shareable Profile Link</h3>
                  <p>Share your profile anywhere—WhatsApp, socials, past students. Own your audience. Not dependent on platform algorithm.</p>
                </div>
              </div>

              <div className="mbl-feature-card">
                <div className="mbl-feature-icon mbl-icon-purple">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v20m10-10H2"/>
                  </svg>
                </div>
                <div className="mbl-feature-body">
                  <h3>Keep 85% of Earnings</h3>
                  <p>We take only 15% per transaction. Withdraw anytime—7-day hold applies (R300 minimum, R5 fee). No hidden fees.</p>
                </div>
              </div>

              <div className="mbl-feature-card">
                <div className="mbl-feature-icon mbl-icon-lime">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 8c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm0 2c2.67 0 8 1.34 8 4v2H4v-2c0-2.66 5.33-4 8-4z"/>
                  </svg>
                </div>
                <div className="mbl-feature-body">
                  <h3>Zero Admin Overhead</h3>
                  <p>No approval process delays. No commission negotiation. No platform jumping through hoops. You're in control.</p>
                </div>
              </div>
            </div>
          </section>

          {/* How it works */}
          <section className="mbl-section">
            <div className="mbl-section-header">
              <h2 className="mbl-section-title">How to get started</h2>
            </div>
            <div className="mbl-steps">
              <div className="mbl-step">
                <div className="mbl-step-num">1</div>
                <div className="mbl-step-body">
                  <h4>Complete Your Profile</h4>
                  <p>Bio, subject, rates. 10 minutes. Your profile goes live immediately.</p>
                </div>
              </div>
              <div className="mbl-step">
                <div className="mbl-step-num">2</div>
                <div className="mbl-step-body">
                  <h4>Set Up 3 Groups</h4>
                  <p>Choose rates (weekly/monthly), group size, availability. Share your profile link.</p>
                </div>
              </div>
              <div className="mbl-step">
                <div className="mbl-step-num">3</div>
                <div className="mbl-step-body">
                  <h4>Start Earning</h4>
                  <p>Students subscribe. Private requests come in. Withdraw weekly. Keep 85%.</p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="mbl-cta-banner mbl-tutor" id="mbl-teach">
            <h3>Ready to build your tutoring business?</h3>
            <p>Join tutors already earning on Kaddle. Set your rates, keep 85%, no commission negotiations. Apply in minutes.</p>
            <a
              href="https://forms.gle/QwWVCDrKL3gbPoYq5"
              target="_blank"
              rel="noopener noreferrer"
              className="mbl-btn-lime"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
            >
              Apply to Teach
            </a>
          </section>

        </div>
      )}

      {/* Large Screen Modal */}
      {modalOpen && (
        <div className="mbl-modal-overlay mbl-active" onClick={handleOverlayClick}>
          <div className="mbl-modal-content">
            <div className="mbl-modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#5b7cfa', width: '48px', height: '48px' }}>
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
            </div>
            <div className="mbl-modal-title">Use on a Larger Screen</div>
            <div className="mbl-modal-text">
              The full Kaddle experience is optimized for tablets, iPads, laptops, and PCs.
            </div>
            <div className="mbl-modal-devices">
              <div className="mbl-device-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                  <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
                Tablet
              </div>
              <div className="mbl-device-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
                iPad
              </div>
              <div className="mbl-device-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
                Laptop
              </div>
              <div className="mbl-device-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
                PC
              </div>
            </div>
            <div className="mbl-modal-text" style={{ fontSize: '0.8rem', marginBottom: '1rem' }}>
              Open Kaddle on one of these devices to browse tutors, join groups, and manage your learning.
            </div>
            <div className="mbl-modal-actions">
              <button className="mbl-btn mbl-btn-primary" onClick={closeModal}>Got it</button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="mbl-footer">
        <div className="mbl-footer-logo">
          <span className="mbl-brand-dot"></span>
          Kaddle
        </div>
        <p>A learning platform built for flexibility.<br/>© 2026 Kaddle. All rights reserved.</p>
      </footer>

      <div className="mbl-safe-bottom"></div>
    </div>
  );
};

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ toasts, removeToast }) => (
  <div className="toast-container">
    {toasts.map(t => (
      <div key={t.id} className={`toast toast-${t.type}`}>
        <span className="toast-icon">
          {t.type === 'success' ? '✓' : t.type === 'error' ? '✕' : 'ℹ'}
        </span>
        <span className="toast-msg">{t.message}</span>
        <button className="toast-close" onClick={() => removeToast(t.id)}>×</button>
      </div>
    ))}
  </div>
);

// ─── Forgot Modal ─────────────────────────────────────────────────────────────
const ForgotModal = ({ onClose, addToast }) => {
  const [forgotEmail, setForgotEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!forgotEmail) {
      addToast("error", "Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/forgot-password`, { email: forgotEmail });
      addToast("success", res.data.message || "Reset link sent! Check your inbox.");
      onClose();
    } catch (err) {
      addToast("error", err.response?.data?.message || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdrop}>
      <div className="modal-box">
        <button className="modal-close-btn" onClick={onClose}>×</button>
        <div className="modal-icon">🔑</div>
        <h3 className="modal-title">Reset your password</h3>
        <p className="modal-sub">Enter the email linked to your account and we'll send you a reset link.</p>
        <input
          type="email"
          className="modal-input"
          placeholder="you@example.com"
          value={forgotEmail}
          onChange={(e) => setForgotEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        />
        <button className="modal-submit-btn" onClick={handleSubmit} disabled={loading}>
          {loading ? <span className="btn-spinner" /> : "Send Reset Link"}
        </button>
      </div>
    </div>
  );
};

// ─── Main AuthPage ────────────────────────────────────────────────────────────
const AuthPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [toasts, setToasts] = useState([]);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const checkViewport = () => setIsMobile(isSmallViewport());
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // ── Toast helpers ────────────────────────────
  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const addToast = (type, message) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => removeToast(id), 4500);
  };

  if (isMobile) return <MobileLandingScreen />;

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      addToast("error", "Please enter both email and password.");
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/login`, {
        email: loginEmail,
        password: loginPassword
      });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      addToast("success", "Welcome back! Redirecting…");
      setTimeout(() => {
        const role = res.data.user.role;
        if (role === "tutee")  navigate("/tutee/dashboard");
        else if (role === "tutor")  navigate("/tutor/dashboard");
        else if (role === "admin")  navigate("/admin/dashboard");
        else navigate("/auth");
      }, 1000);
    } catch (err) {
      addToast("error", err.response?.data?.message || "Login failed. Check your credentials.");
    }
  };

  const goToTerms = () => navigate('/Terms');
  const goToAboutUs = () => navigate('/idea');

  const handleRegister = async () => {
    if (!firstName || !lastName || !regEmail || !regPassword || !confirmPassword) {
      addToast("error", "Please fill in all fields.");
      return;
    }
    if (regPassword !== confirmPassword) {
      addToast("error", "Passwords do not match.");
      return;
    }
    try {
      await axios.post(`${API_URL}/register/tutee`, {
        firstName, lastName, email: regEmail, password: regPassword, role: "tutee"
      });
      addToast("success", "Account created! Please log in.");
      setActiveTab("login");
      setFirstName(""); setLastName(""); setRegEmail("");
      setRegPassword(""); setConfirmPassword("");
    } catch (err) {
      addToast("error", err.response?.data?.message || "Registration failed. Try again.");
    }
  };

  return (
    <div className="auth-page">
      <Toast toasts={toasts} removeToast={removeToast} />
      {showForgotModal && (
        <ForgotModal onClose={() => setShowForgotModal(false)} addToast={addToast} />
      )}

      {/* Left Panel */}
      <div className="left-panel">
        <div className="left-inner">
          <div className="brand">
            <span className="brand-dot" />
            <span className="brand-name">myKaddle</span>
          </div>

          <div className="form-tabs">
            <button
              className={`tab-btn ${activeTab === 'login' ? 'active' : ''}`}
              onClick={() => setActiveTab('login')}
            >Login</button>
            <button
              className={`tab-btn ${activeTab === 'register' ? 'active' : ''}`}
              onClick={() => setActiveTab('register')}
            >Register</button>
          </div>

          {activeTab === 'login' && (
            <div className="form-body animate-in">
              <h2 className="form-heading">Welcome back</h2>
              <p className="form-sub">Sign in to continue learning</p>
              <div className="field-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                />
              </div>
              <div className="field-group">
                <label>Password</label>
                <div className="password-wrapper">
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  />
                  <button className="eye-btn" onClick={() => setShowLoginPassword(!showLoginPassword)}>
                    {showLoginPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <button className="forgot-link" onClick={() => setShowForgotModal(true)}>
                Forgot password?
              </button>
              <button className="primary-btn" onClick={handleLogin}>Sign In</button>
            </div>
          )}

          {activeTab === 'register' && (
            <div className="form-body animate-in">
              <h2 className="form-heading">Create account</h2>
              <p className="form-sub">Join as a tutee for free</p>
              <div className="name-row">
                <div className="field-group">
                  <label>First Name</label>
                  <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </div>
                <div className="field-group">
                  <label>Last Name</label>
                  <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>
              </div>
              <div className="field-group">
                <label>Email</label>
                <input type="email" placeholder="you@example.com" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} />
              </div>
              <div className="field-group">
                <label>Password</label>
                <div className="password-wrapper">
                  <input
                    type={showRegPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                  />
                  <button className="eye-btn" onClick={() => setShowRegPassword(!showRegPassword)}>
                    {showRegPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <div className="field-group">
                <label>Confirm Password</label>
                <div className="password-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button className="eye-btn" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <button className="primary-btn" onClick={handleRegister}>Create Account</button>
              <p className="terms-note">
                By signing up you agree to our <a onClick={goToTerms}>Terms &amp; Conditions</a>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel */}
      <div className="right-panel">
        <div className="right-inner">
          <div className="rp-word-stack">
            <span className="rp-word">Connect.</span>
            <span className="rp-word">Learn.</span>
            <span className="rp-word">Grow.</span>
          </div>
          <p className="rp-tagline">
            Kaddle is built to create a vibrant and supportive learning community where tutors inspire learners,
            and learners grow together through collaboration and shared knowledge.
          </p>
          <div className="guidelines-grid">
            {[
              { color: "#FF6B6B", title: "Respectful Community", desc: "Treat every member with kindness in chats and classes." },
              { color: "#118AB2", title: "About Us", desc: "Learn more about our mission and vision — click to read our story.", onClick: goToAboutUs },
            ].map((item, i) => (
              <div 
                className="guide-card" 
                key={i} 
                style={{ 
                  animationDelay: `${i * 0.08}s`,
                  cursor: item.onClick ? 'pointer' : 'default'
                }}
                onClick={item.onClick}
              >
                <span className="guide-icon" style={{ color: item.color }}>✦</span>
                <div>
                  <p className="guide-title">{item.title}</p>
                  <p className="guide-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;